import { useEffect, useMemo, useRef, useState } from 'react'
import { uniqBy, last } from 'lodash'

/**
 * TODO: Fix typings to infer PaginationResult and PaginationQueryArg
 * The idea is to normalize by transforming query result
 */
export const useInfiniteScrollQuerySimple = <T extends { id: any }>({
  useQuery,
  args,
  dataKey = 'list',
  paginationKey = 'from',
  nextPageKey = 'next_page_value',
  idKey = 'id',
  objKey = null,
  groupMembersScroll = false, // This param exists because API related to group members handles pagination differently
}) => {
  const [list, setList] = useState<Array<T>>([])
  const [fromId, setFromId] = useState<number>(null)

  const queryRes = useQuery({
    [paginationKey]: fromId,
    ...args,
  })

  const resData = useMemo(() => {
    return queryRes?.data ?? {}
  }, [queryRes?.data])

  const loadMore = () => {
    if (!groupMembersScroll) {
      setFromId(queryRes?.data?.[nextPageKey])
    } else if (objKey) {
      setFromId(last(queryRes?.data)[objKey][idKey])
    } else {
      setFromId(last(queryRes?.data)[idKey])
    }
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    setFromId(null)
    setList([])
  }, [args])

  useEffect(() => {
    var currentQueryResData, iteratee

    if (!groupMembersScroll) {
      currentQueryResData = queryRes?.data?.[dataKey]
      iteratee = (x) => x?.[idKey]
    } else if (objKey) {
      currentQueryResData = queryRes?.data
      iteratee = (x) => x?.[objKey][idKey]
    } else {
      currentQueryResData = queryRes?.data
      iteratee = (x) => x?.[idKey]
    }

    setList(
      uniqBy(
        [
          ...list?.map?.((x) => {
            if (!groupMembersScroll) {
              return (
                queryRes?.data?.[dataKey]?.find?.(
                  (y: any) => y?.[idKey] == x?.[idKey]
                ) ?? x
              )
            } else if (objKey) {
              return (
                queryRes?.data?.find?.(
                  // No dataKey necessary: Group members query returns list at top level of data
                  (y: any) => y?.[objKey][idKey] == x?.[objKey][idKey]
                ) ?? x
              )
            } else {
              return (
                queryRes?.data?.find?.((y: any) => y?.[idKey] == x?.[idKey]) ??
                x
              )
            }
          }),
          ...(currentQueryResData ?? []),
        ],
        iteratee
      )
    )
  }, [queryRes?.data])

  const setListItem = (id: any, item: any) => {
    setList((prevList) =>
      prevList
        .map((i) => {
          if (i?.[idKey] === id) {
            return item
          }

          return i
        })
        .filter(Boolean)
    )
  }

  return {
    loadMore,
    loadMoreRef,
    list,
    data: resData,
    setList,
    setListItem,
    isLoading: queryRes.isLoading,
    isFetching: queryRes.isFetching,
    isEmpty: !queryRes.isLoading && list?.length === 0,
    hasMore: groupMembersScroll
      ? queryRes?.data?.length > 0
      : queryRes?.data?.[nextPageKey],
  }
}

export const useInfinityScrollSimple = ({
  loadMore,
}: {
  loadMore: Function
}) => {
  const observer = useRef<IntersectionObserver>()
  const loadMoreRef = useRef()

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (elements) => {
        const $target = elements?.[0]

        if ($target?.intersectionRatio > 0) {
          // Slight delay to prevent excessively load more
          // in case loaded items get hidden, which makes
          // the load more component still visible in the viewport.
          window.setTimeout(() => {
            loadMore()
          }, 200)
        }
      },
      {
        rootMargin: '50px 0px',
      }
    )

    if (observer.current && loadMoreRef.current) {
      observer.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [loadMore])

  return {
    loadMoreRef,
  }
}
